import { Route, Routes, useLocation } from 'react-router-dom';
import { useApplicationState } from './App.state';

import { TransactionStatusType } from '@bus/models';
import { QueryClientProvider } from 'react-query';
import { MatchMakerQuestion1 } from './MatchMaker/QuestionPages/MatchMakerQuestion1';
import { MatchMakerQuestion2 } from './MatchMaker/QuestionPages/MatchMakerQuestion2';
import WithDataLoaded from './WithDataLoaded';
import { ContinueLaterPopup } from './components/ContinueLaterPopup/ContinueLaterPopup';
import { TransactionCompletedNotification } from './lib/components/TransactionCompletedNotification';
import { Confirmation } from './pages/Confirmation';
import { CustomerInformation } from './pages/CustomerInformation';
import { DealerSelection } from './pages/DealerSelection';
import ErrorPage404 from './pages/ErrorPages/ErrorPage404';
import { JourneyHeroTeaser } from './pages/components/HeroTeaser/JourneyHeroTeaser';
import MasterPage from './pages/MasterPage';
import { SummaryPage } from './pages/SummaryPage';
import WizardContainer from './pages/components/Wizard/WizardContainer';
import EntryPoint from './router/EntryPoint';
import { RouterHelpers } from './router/router-helpers';
import LinkExpiredPage from './pages/LinkExpiredPage/LinkExpiredPage';
import { ContentSection, SkipLink } from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { queryClient } from './queryClient';

import './../RenewalApp/MatchMaker/MatchMaker.scss';

function App(): JSX.Element {
  const { state } = useApplicationState();

  const { t } = useTranslation('common');
  const isTransactionCompleted = state.transactionStatus === TransactionStatusType.COMPLETED;
  const location = useLocation();
  const [skipToHref, setSkipToHref] = useState('');

  const isOnMM1 =
    location.pathname === RouterHelpers.getMatchMakerQuestionPage1Path() ||
    location.pathname === RouterHelpers.getMatchMakerQuestionPage1EditPath();

  const shouldShowContinueLater = !isTransactionCompleted && (!isOnMM1 || state.hasSubmittedMM1);

  useEffect(() => {
    setSkipToHref(
      window.location.href.endsWith('#main-content')
        ? window.location.href.substring(window.location.origin.length)
        : `${window.location.href.substring(window.location.origin.length)}#main-content`
    );
  }, [location]);

  return (
    <MasterPage>
      <WithDataLoaded>
        <QueryClientProvider client={queryClient}>
          <SkipLink
            label={t('shared:skip-to-main-content')}
            href={skipToHref}
            onClick={() => {
              document.getElementById('main-content')?.focus();
            }}
          />
          <JourneyHeroTeaser />
          <WizardContainer />
          <TransactionCompletedNotification />
          <ContentSection pageWrap>
            <div id="main-content" tabIndex={-1}>
              <Routes>
                <Route
                  path={RouterHelpers.getEntryPointPath()}
                  element={
                    <EntryPoint
                      isTransactionCompleted={isTransactionCompleted}
                      legalEntity={state.legalEntity}
                    />
                  }
                />
                <Route
                  path={RouterHelpers.getMatchMakerQuestionPage1Path()}
                  element={<MatchMakerQuestion1 />}
                />
                <Route
                  path={RouterHelpers.getMatchMakerQuestionPage2Path()}
                  element={<MatchMakerQuestion2 />}
                />
                <Route
                  path={RouterHelpers.getMatchMakerQuestionPage1EditPath()}
                  element={<MatchMakerQuestion1 isEditMode />}
                />
                <Route
                  path={RouterHelpers.getMatchMakerQuestionPage2EditPath()}
                  element={<MatchMakerQuestion2 isEditMode />}
                />
                <Route
                  path={RouterHelpers.getDealerSelectionPagePath()}
                  element={<DealerSelection />}
                />
                <Route
                  path={RouterHelpers.getDealerSelectionEditPagePath()}
                  element={<DealerSelection isEditMode />}
                />
                <Route
                  path={RouterHelpers.getPersonalDetailsPath()}
                  element={<CustomerInformation />}
                />
                <Route
                  path={RouterHelpers.getPersonalDetailsEditPagePath()}
                  element={<CustomerInformation isEditMode />}
                />
                <Route path={RouterHelpers.getSummaryPagePath()} element={<SummaryPage />} />
                <Route path={RouterHelpers.getConfirmationPagePath()} element={<Confirmation />} />
                <Route
                  path={RouterHelpers.getLinkExpiredPagePath()}
                  element={<LinkExpiredPage />}
                />
                <Route
                  path="*"
                  element={
                    <ErrorPage404 noId={!state.transactionId} noLegalEntity={!state.legalEntity} />
                  }
                />
              </Routes>
            </div>
          </ContentSection>

          {shouldShowContinueLater && <ContinueLaterPopup />}
        </QueryClientProvider>
      </WithDataLoaded>
    </MasterPage>
  );
}

export default App;
